import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, Companion } from '../store/gameStore';

const CompanionCardSelection: React.FC = () => {
  const { companions, selectCompanionCard, gems, startCompanionCardDraw, formatNumber } = useGameStore();
  const [selectedCard, setSelectedCard] = useState<Companion | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [result, setResult] = useState<{ success: boolean; companion?: Companion } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Dynamische Kosten: mindestens 10.000 oder 10% der aktuellen Diamanten
  const CARD_GAME_COST = Math.max(10000, Math.floor(gems * 0.1));
  const canAfford = gems >= CARD_GAME_COST;

  // Prüfe ob es ein kostenloser Zug ist
  const isFreeDrawActive = companions.freeDrawLastUsed && 
    !companions.freeDrawAvailable && 
    companions.pendingCards;

  const handleStartGame = () => {
    if (!canAfford || isPlaying) return;
    
    // Starte das Kartenspiel (zahle Diamanten und erstelle Karten)
    if (startCompanionCardDraw) {
      startCompanionCardDraw(CARD_GAME_COST);
      setIsPlaying(true);
    }
  };

  // Setze isPlaying zurück, wenn das Spiel beendet ist
  useEffect(() => {
    if (isPlaying && (!companions.pendingCards || companions.pendingCards.length === 0)) {
      setIsPlaying(false);
    }
  }, [companions.pendingCards, isPlaying]);

  // Wenn keine Karten vorhanden sind, zeige Start-Ansicht
  if (!companions.pendingCards) {
    return (
      <div className="relative bg-gradient-to-br from-slate-800/90 via-purple-900/80 to-slate-900/90 backdrop-blur-xl border border-purple-500/30 p-4 sm:p-6 rounded-2xl w-full overflow-hidden overscroll-none shadow-2xl">
        {/* Glasmorphismus Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-purple-500/10 rounded-2xl pointer-events-none" />
        
        <div className="relative text-center">
          
          <div className="bg-purple-800/30 rounded-lg p-4 mb-4">
            <div className="text-4xl mb-3">🎴</div>
            <h4 className="text-white font-bold text-xl mb-2">Mysteriöse Karten-Auswahl</h4>
            <p className="text-purple-200 text-sm mb-4">
              Wähle aus 3 mysteriösen Karten - eine könnte einen mächtigen Begleiter enthalten!
            </p>
            
            {/* Gewinnchancen-Übersicht */}
            <div className="bg-gray-800/50 rounded-lg p-3 mb-4 border border-purple-500/30">
              <h5 className="text-purple-300 font-bold text-sm mb-2">🎯 Gewinnchancen</h5>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-green-600/20 rounded px-2 py-1 border border-green-500/30">
                  <div className="text-green-300 font-semibold">50% Gewinn</div>
                  <div className="text-green-200 text-xs">Begleiter erhalten</div>
                </div>
                <div className="bg-red-600/20 rounded px-2 py-1 border border-red-500/30">
                  <div className="text-red-300 font-semibold">50% Leer</div>
                  <div className="text-red-200 text-xs">Kein Begleiter</div>
                </div>
              </div>
              
              {/* Begleiter-Typen Verteilung */}
              <div className="mt-3 pt-2 border-t border-purple-500/30">
                <div className="text-purple-300 font-semibold text-xs mb-2">Bei Gewinn:</div>
                <div className="grid grid-cols-2 gap-1 text-xs">
                  <div className="text-gray-300">
                    <span className="text-gray-400">⚪</span> 30% Gewöhnlich
                  </div>
                  <div className="text-blue-300">
                    <span className="text-blue-400">💎</span> 29% Selten
                  </div>
                  <div className="text-purple-300">
                    <span className="text-purple-400">💜</span> 28% Episch
                  </div>
                  <div className="text-yellow-300">
                    <span className="text-yellow-400">✨</span> 12% Legendär
                  </div>
                  <div className="text-orange-300 col-span-2 text-center">
                    <span className="text-orange-400">⭐</span> 1% Göttlich Legendär
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-white">Deine Diamanten:</span>
              <span className="text-purple-300 font-bold">{formatNumber(gems)} 💎</span>
            </div>
            
            {/* Kosten-Anzeige */}
            <div className="bg-yellow-600/20 rounded-lg p-2 mb-4 border border-yellow-500/30">
              <div className="text-yellow-300 font-bold text-sm">💰 Kosten</div>
              <div className="text-yellow-200 text-xs">
                {formatNumber(CARD_GAME_COST)} 💎 (10% deiner Diamanten, min. 10.000)
              </div>
            </div>
          </div>
          
          <motion.button
            onClick={handleStartGame}
            disabled={!canAfford || isPlaying}
            className={`relative w-full py-4 px-6 rounded-xl font-bold text-xl transition-all duration-300 border-2 ${
              canAfford && !isPlaying
                ? 'bg-gradient-to-b from-purple-500 via-purple-600 to-blue-700 hover:from-purple-600 hover:via-purple-700 hover:to-blue-800 text-white shadow-2xl border-purple-300'
                : 'bg-gradient-to-b from-gray-400 to-gray-600 text-gray-300 cursor-not-allowed border-gray-500'
            }`}
            whileHover={canAfford && !isPlaying ? { scale: 1.02 } : {}}
            whileTap={canAfford && !isPlaying ? { scale: 0.98 } : {}}
          >
            <div className="relative z-10 flex items-center justify-center">
              {isPlaying ? (
                <div className="flex items-center gap-2">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                    className="text-3xl"
                  >
                    🎴
                  </motion.div>
                  <span className="text-xl tracking-wider">KARTEN WERDEN ERSTELLT...</span>
                </div>
              ) : canAfford ? (
                <div className="text-center">
                  <div className="text-2xl mb-1">🎴 KARTEN ZIEHEN!</div>
                  <div className="text-sm opacity-90">{formatNumber(CARD_GAME_COST)} 💎</div>
                  <div className="text-xs opacity-75 mt-1">50% Gewinnchance</div>
                </div>
              ) : (
                <div className="text-center text-gray-400">
                  <div className="text-lg mb-1">💎 NICHT GENUG</div>
                  <div className="text-sm">Brauche: {formatNumber(CARD_GAME_COST)} 💎</div>
                </div>
              )}
            </div>
          </motion.button>
          

        </div>
      </div>
    );
  }

  const handleCardSelect = (companion: Companion) => {
    if (isRevealing || result || selectedCard) return;
    
    setSelectedCard(companion);
    setIsRevealing(true);
    
    // Warte 2 Sekunden für die Animation
    setTimeout(() => {
      const success = selectCompanionCard(companion);
      setResult({ success, companion: success ? companion : undefined });
      setIsRevealing(false);
    }, 2000);
  };

  const handleClose = () => {
    setSelectedCard(null);
    setIsRevealing(false);
    setResult(null);
  };

  const getRarityColor = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'from-yellow-300 via-orange-400 to-red-500';
      case 'epic': return 'from-purple-400 via-pink-500 to-purple-600';
      case 'rare': return 'from-blue-400 via-cyan-500 to-teal-500';
      case 'common': return 'from-gray-400 via-slate-500 to-gray-600';
      default: return 'from-gray-400 via-slate-500 to-gray-600';
    }
  };

  const getRarityBorder = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'border-yellow-400 shadow-yellow-400/60 shadow-2xl';
      case 'epic': return 'border-purple-400 shadow-purple-400/60 shadow-2xl';
      case 'rare': return 'border-blue-400 shadow-blue-400/60 shadow-2xl';
      case 'common': return 'border-gray-400 shadow-gray-400/60 shadow-xl';
      default: return 'border-gray-400 shadow-gray-400/60 shadow-xl';
    }
  };

  const getRarityGlow = (type: Companion['type']) => {
    switch (type) {
      case 'legendary': return 'drop-shadow-[0_0_20px_rgba(251,191,36,0.8)]';
      case 'epic': return 'drop-shadow-[0_0_20px_rgba(168,85,247,0.8)]';
      case 'rare': return 'drop-shadow-[0_0_20px_rgba(59,130,246,0.8)]';
      case 'common': return 'drop-shadow-[0_0_15px_rgba(156,163,175,0.6)]';
      default: return 'drop-shadow-[0_0_15px_rgba(156,163,175,0.6)]';
    }
  };

  return (
    <div className="relative bg-gradient-to-br from-slate-800/90 via-purple-900/80 to-slate-900/90 backdrop-blur-xl border border-purple-500/30 p-4 sm:p-6 rounded-2xl w-full overflow-hidden overscroll-none shadow-2xl">
      {/* Glasmorphismus Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-purple-500/10 rounded-2xl pointer-events-none" />
      
      {!result ? (
        <>
          <div className="relative text-center mb-4 sm:mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent blur-sm" />
              <p className="relative text-purple-200 text-sm sm:text-base lg:text-lg px-4 leading-tight font-medium">
                {isFreeDrawActive 
                  ? "🎁 Kostenloser Zug! Du erhältst garantiert einen mächtigen Begleiter!"
                  : "🔮 Wähle eine Karte und lass das Schicksal entscheiden!"
                }
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            {companions.pendingCards.map((companion, index) => (
              <motion.div
                key={companion.id}
                initial={{ opacity: 0, y: 30, rotateY: -15 }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ 
                  delay: index * 0.15, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100
                }}
                className="relative h-48 sm:h-56 lg:h-64 perspective-1000"
              >
                {/* Zeige Kartenrückseite wenn nicht ausgewählt */}
                {selectedCard !== companion && (
                  <motion.div
                    className="w-full h-full rounded-xl cursor-pointer relative overflow-hidden
                      bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-600
                      border-2 border-purple-400/50 shadow-2xl
                      transform-gpu backface-hidden select-none touch-manipulation"
                    onClick={() => handleCardSelect(companion)}
                    whileHover={{ 
                      scale: 1.05, 
                      rotateY: 5,
                      boxShadow: "0 25px 50px -12px rgba(168, 85, 247, 0.5)"
                    }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  >
                    {/* Animated gradient overlay */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-br from-purple-400/30 via-transparent to-pink-400/30"
                      animate={{
                        background: [
                          "linear-gradient(45deg, rgba(168,85,247,0.3), transparent, rgba(236,72,153,0.3))",
                          "linear-gradient(135deg, rgba(236,72,153,0.3), transparent, rgba(168,85,247,0.3))",
                          "linear-gradient(225deg, rgba(168,85,247,0.3), transparent, rgba(236,72,153,0.3))",
                          "linear-gradient(315deg, rgba(236,72,153,0.3), transparent, rgba(168,85,247,0.3))"
                        ]
                      }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                    />
                    
                    {/* Card pattern */}
                    <div className="absolute inset-0 opacity-20">
                      <div className="w-full h-full bg-[radial-gradient(circle_at_50%_50%,_rgba(255,255,255,0.1)_1px,_transparent_1px)] bg-[length:20px_20px]" />
                    </div>
                    
                    <div className="relative z-10 flex items-center justify-center h-full text-center text-white p-4">
                      <div>
                        <motion.div 
                          className="text-3xl sm:text-4xl lg:text-5xl mb-2 sm:mb-3 filter drop-shadow-lg"
                          animate={{ 
                            scale: [1, 1.1, 1],
                            rotate: [0, 5, -5, 0]
                          }}
                          transition={{ 
                            duration: 3, 
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          🎴
                        </motion.div>
                        <motion.div 
                          className="text-sm sm:text-base font-bold mb-1 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          Mysteriöse Karte
                        </motion.div>
                        <motion.div 
                          className="text-xs sm:text-sm opacity-90 font-medium"
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          ✨ Antippen zum Aufdecken ✨
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Zeige Kartenvorderseite wenn ausgewählt */}
                {selectedCard === companion && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0, rotateY: 180 }}
                    animate={{ scale: 1, opacity: 1, rotateY: 0 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 200, 
                      damping: 20,
                      duration: 0.8
                    }}
                    className={`w-full h-full rounded-xl border-3 p-2 sm:p-3 relative overflow-hidden
                      bg-gradient-to-br ${getRarityColor(companion.type)}
                      ${getRarityBorder(companion.type)} select-none transform-gpu`}
                  >
                    {/* Rarity glow effect */}
                    <div className={`absolute inset-0 ${getRarityGlow(companion.type)} opacity-50 pointer-events-none`} />
                    
                    {/* Sparkle animation overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      {[...Array(8)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1 h-1 bg-white rounded-full"
                          animate={{
                            scale: [0, 1, 0],
                            opacity: [0, 1, 0],
                            x: [0, Math.random() * 100 - 50],
                            y: [0, Math.random() * 100 - 50],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: i * 0.2,
                            ease: "easeOut"
                          }}
                          style={{
                            left: Math.random() * 100 + '%',
                            top: Math.random() * 100 + '%',
                          }}
                        />
                      ))}
                    </div>

                    <div className="relative z-10 text-center text-white h-full flex flex-col justify-between">
                      <div className="flex-shrink-0">
                        <motion.div 
                          className="text-2xl sm:text-3xl mb-1 sm:mb-2 filter drop-shadow-lg"
                          animate={{ 
                            scale: [1, 1.2, 1],
                            rotate: [0, 10, -10, 0]
                          }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity,
                            ease: "easeInOut"
                          }}
                        >
                          {companion.icon.startsWith('data:') ? (
                            <img 
                              src={companion.icon} 
                              alt={companion.name}
                              className="w-8 h-8 sm:w-12 sm:h-12 object-contain"
                              style={{ display: 'inline-block' }}
                            />
                          ) : (
                            companion.icon
                          )}
                        </motion.div>
                        <motion.h3 
                          className="text-sm sm:text-base lg:text-lg font-bold mb-1 leading-tight drop-shadow-lg"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          {companion.name}
                        </motion.h3>
                        <motion.div 
                          className="text-xs opacity-90 capitalize mb-2 font-semibold bg-black/30 rounded-full px-2 py-1 inline-block"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          {companion.type === 'legendary' ? '✨ Legendär ✨' : 
                           companion.type === 'epic' ? '💜 Episch 💜' : 
                           companion.type === 'rare' ? '💎 Selten 💎' : '⚪ Gewöhnlich ⚪'}
                        </motion.div>
                      </div>
                      
                      <motion.div 
                        className="space-y-1 overflow-y-auto max-h-20 sm:max-h-24 flex-1 scrollbar-thin scrollbar-thumb-white/40 scrollbar-track-transparent pr-1"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.7 }}
                      >
                        {companion.abilities.slice(0, 3).map((ability, idx) => (
                          <motion.div 
                            key={idx} 
                            className="text-xs bg-black/40 backdrop-blur-sm rounded-lg px-2 py-1 leading-tight border border-white/20 font-medium"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.8 + idx * 0.1 }}
                          >
                            {ability.description}
                          </motion.div>
                        ))}
                        {companion.abilities.length > 3 && (
                          <div className="text-xs text-white/70 text-center">
                            +{companion.abilities.length - 3} weitere...
                          </div>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>

          {isRevealing && (
            <motion.div 
              className="text-center mt-4 sm:mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <motion.div
                animate={{ 
                  rotate: 360,
                  scale: [1, 1.2, 1]
                }}
                transition={{ 
                  rotate: { duration: 2, ease: "linear" },
                  scale: { duration: 1, repeat: Infinity, ease: "easeInOut" }
                }}
                className="text-3xl sm:text-4xl mb-2 sm:mb-3 filter drop-shadow-[0_0_20px_rgba(251,191,36,0.8)]"
              >
                ✨
              </motion.div>
              <motion.p 
                className="text-purple-200 text-sm sm:text-base lg:text-lg font-semibold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                🔮 Das Schicksal entscheidet... 🔮
              </motion.p>
            </motion.div>
          )}
        </>
      ) : (
        /* Ergebnis-Anzeige */
        <motion.div
          initial={{ scale: 0, opacity: 0, rotateY: 180 }}
          animate={{ scale: 1, opacity: 1, rotateY: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="text-center px-2 relative"
        >
          {result.success ? (
            <div>
              <motion.div
                animate={{ 
                  scale: [1, 1.3, 1.1, 1.3, 1],
                  rotate: [0, 10, -10, 10, 0]
                }}
                transition={{ duration: 1, repeat: 2 }}
                className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 filter drop-shadow-[0_0_30px_rgba(251,191,36,1)]"
              >
                🎉
              </motion.div>
              
              {/* Confetti effect */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(20)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 bg-gradient-to-r from-yellow-400 to-pink-500 rounded-full"
                    animate={{
                      y: [-20, window.innerHeight],
                      x: [0, Math.random() * 200 - 100],
                      rotate: [0, 360],
                      scale: [1, 0]
                    }}
                    transition={{
                      duration: 3,
                      delay: i * 0.1,
                      ease: "easeOut"
                    }}
                    style={{
                      left: Math.random() * 100 + '%',
                      top: '10%',
                    }}
                  />
                ))}
              </div>

              <motion.h2 
                className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500 bg-clip-text text-transparent mb-2 sm:mb-3 drop-shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                🏆 Glückwunsch! 🏆
              </motion.h2>
              <motion.p 
                className="text-purple-200 text-base sm:text-lg lg:text-xl mb-4 sm:mb-6 px-2 leading-tight font-medium"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                🎊 Du hast <span className="font-bold text-yellow-300">{result.companion?.name}</span> als Begleiter erhalten! 🎊
              </motion.p>
              
              <motion.div 
                className={`inline-block p-3 sm:p-4 lg:p-6 rounded-2xl border-3 mx-1 sm:mx-2 max-w-sm sm:max-w-md relative overflow-hidden
                  bg-gradient-to-br ${getRarityColor(result.companion?.type || 'common')}
                  ${getRarityBorder(result.companion?.type || 'common')}`}
                initial={{ scale: 0, rotateY: 180 }}
                animate={{ scale: 1, rotateY: 0 }}
                transition={{ delay: 1, type: "spring", stiffness: 200 }}
              >
                {/* Winner glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/10 pointer-events-none" />
                
                <motion.div 
                  className="text-3xl sm:text-4xl lg:text-5xl mb-2 sm:mb-3 filter drop-shadow-lg"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 15, -15, 0]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  {result.companion?.icon.startsWith('data:') ? (
                    <img 
                      src={result.companion.icon} 
                      alt={result.companion.name}
                      className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-contain"
                      style={{ display: 'inline-block' }}
                    />
                  ) : (
                    result.companion?.icon
                  )}
                </motion.div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3 leading-tight drop-shadow-lg">
                  {result.companion?.name}
                </h3>
                <div className="space-y-1 max-h-24 sm:max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-white/40 scrollbar-track-transparent">
                  {result.companion?.abilities.slice(0, 3).map((ability, idx) => (
                    <motion.div 
                      key={idx} 
                      className="text-white bg-black/40 backdrop-blur-sm rounded-lg px-2 py-1 text-xs sm:text-sm leading-tight border border-white/30 font-medium"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 + idx * 0.1 }}
                    >
                      ✨ {ability.description}
                    </motion.div>
                  ))}
                  {result.companion && result.companion.abilities.length > 3 && (
                    <div className="text-xs text-white/70 text-center">
                      +{result.companion.abilities.length - 3} weitere Fähigkeiten...
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          ) : (
            <div>
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, -10, 10, 0]
                }}
                transition={{ duration: 1, repeat: 1 }}
                className="text-4xl sm:text-5xl lg:text-6xl mb-3 sm:mb-4 filter drop-shadow-[0_0_20px_rgba(239,68,68,0.8)]"
              >
                💔
              </motion.div>
              <motion.h2 
                className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-red-400 to-pink-500 bg-clip-text text-transparent mb-2 sm:mb-3 drop-shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                😔 Kein Glück diesmal... 😔
              </motion.h2>
              <motion.p 
                className="text-purple-200 text-base sm:text-lg lg:text-xl mb-4 sm:mb-6 px-2 leading-tight font-medium"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                🌟 Die Karte war leer. Das Glück wird beim nächsten Mal auf deiner Seite sein! 🌟
              </motion.p>
            </div>
          )}
          
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 40px -12px rgba(34, 197, 94, 0.5)"
            }}
            whileTap={{ scale: 0.95 }}
            onClick={handleClose}
            className="relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 active:from-green-600 active:to-emerald-700 text-white font-bold py-3 px-6 sm:py-3 sm:px-8 lg:px-10 rounded-xl mt-4 sm:mt-6 text-sm sm:text-base min-h-[48px] touch-manipulation select-none border border-green-400/30 shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: result?.success ? 1.5 : 0.7 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 transform -skew-x-12 translate-x-full group-hover:translate-x-[-200%] transition-transform duration-1000" />
            <span className="relative z-10">🚀 Weiter spielen 🚀</span>
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default CompanionCardSelection; 